import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, formatCOP } from "@/lib/api";
import {
  LogOut, Shield, UserPlus, Users, DollarSign, ShoppingBag, Loader2,
  Ticket, Power, Settings, Plus, Hash, CheckCircle2, MessageCircle,
  Pencil, Trash2, Wallet, AlertTriangle, X, Bell, BellRing, BellOff, Smartphone,
} from "lucide-react";
import FichaMap from "@/components/FichaMap";
import SiteImagesManager from "@/components/SiteImagesManager";
import { openReceipt } from "@/lib/receipt";
import useReservationAlerts from "@/hooks/useReservationAlerts";
import usePushSubscription from "@/hooks/usePushSubscription";

const PAYMENTS = ["Efectivo", "Nequi", "Daviplata", "Transferencia", "Tarjeta"];

const emptyPick = { open: false, mode: "register", ficha: "", buyer_name: "", buyer_phone: "", payment_method: "Efectivo", seller_id: "" };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [sales, setSales] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", codigo_referido: "" });
  const [creating, setCreating] = useState(false);
  const [editSeller, setEditSeller] = useState(null); // { id, name, codigo_referido }
  const [cfg, setCfg] = useState({ total: "", sold: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [saleForm, setSaleForm] = useState({ ficha_number: "", buyer_name: "", buyer_phone: "", payment_method: "Efectivo" });
  const [savingSale, setSavingSale] = useState(false);
  const [resPay, setResPay] = useState({});
  const [busyRes, setBusyRes] = useState("");
  const [pick, setPick] = useState(emptyPick);
  const [pickBusy, setPickBusy] = useState(false);

  // Real-time alerts when a new reservation comes in
  const { newCount, resetNewCount } = useReservationAlerts({
    intervalMs: 15000,
    onNew: () => load(),
  });

  // Web Push (mobile notifications when the panel is closed)
  const push = usePushSubscription();

  const scrollToReservations = () => {
    resetNewCount();
    document.getElementById("admin-reservations-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const load = async () => {
    try {
      const [su, se, sa, rs] = await Promise.all([
        api.get("/admin/summary"),
        api.get("/admin/sellers"),
        api.get("/admin/sales"),
        api.get("/admin/reservations"),
      ]);
      setSummary(su.data);
      setSellers(se.data);
      setSales(sa.data);
      setReservations(rs.data);
      setCfg({ total: String(su.data.fichas_total), sold: String(su.data.base_sold) });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const closePick = () => setPick(emptyPick);
  const refresh = () => {
    setMapKey((k) => k + 1);
    load();
  };

  // Called by FichaMap when admin clicks any cell
  const handleCellClick = ({ ficha, state, info }) => {
    if (state === "free") {
      setPick({ ...emptyPick, open: true, mode: "register", ficha });
      return;
    }
    if (state === "reserved") {
      setPick({
        ...emptyPick,
        open: true,
        mode: "reserved",
        ficha,
        buyer_name: info?.buyer_name || "",
        buyer_phone: info?.buyer_phone || "",
      });
      return;
    }
    // sold → load full sale record to edit
    const sale = sales.find((s) => s.ficha_number === ficha);
    if (!sale) {
      toast.error("No se encontró el registro de esta venta");
      return;
    }
    setPick({
      open: true,
      mode: "edit",
      ficha,
      buyer_name: sale.buyer_name || "",
      buyer_phone: sale.buyer_phone || "",
      payment_method: sale.payment_method || "Efectivo",
      seller_id: sale.seller_id || "",
    });
  };

  const cancelReservation = async (ficha) => {
    try {
      await api.delete(`/reservations/${ficha}`);
      toast(`Reserva #${ficha} cancelada`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const registerSale = async (e) => {
    e.preventDefault();
    if (!saleForm.ficha_number.trim() || !saleForm.buyer_name.trim()) {
      toast.error("Ingresa el número de ficha y el nombre del comprador");
      return;
    }
    setSavingSale(true);
    try {
      const { data: sale } = await api.post("/sales", saleForm);
      toast.success(`Venta registrada · Ficha #${String(saleForm.ficha_number).padStart(4, "0")}`, {
        description: "Envía el comprobante al comprador",
        action: { label: "Comprobante WhatsApp", onClick: () => openReceipt(sale) },
        duration: 12000,
      });
      setSaleForm({ ficha_number: "", buyer_name: "", buyer_phone: "", payment_method: "Efectivo" });
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingSale(false);
    }
  };

  const submitPick = async (e) => {
    e.preventDefault();
    setPickBusy(true);
    try {
      if (pick.mode === "register") {
        if (!pick.buyer_name.trim()) throw { response: { data: { detail: "Ingresa el nombre del comprador" } } };
        const { data: sale } = await api.post("/sales", {
          ficha_number: pick.ficha,
          buyer_name: pick.buyer_name,
          buyer_phone: pick.buyer_phone,
          payment_method: pick.payment_method,
        });
        toast.success(`Venta registrada · Ficha #${pick.ficha}`, {
          description: "Envía el comprobante al comprador",
          action: { label: "Comprobante WhatsApp", onClick: () => openReceipt(sale) },
          duration: 12000,
        });
      } else if (pick.mode === "edit") {
        const { data: sale } = await api.patch(`/admin/sales/${pick.ficha}`, {
          buyer_name: pick.buyer_name,
          buyer_phone: pick.buyer_phone,
          payment_method: pick.payment_method,
          seller_id: pick.seller_id || undefined,
        });
        toast.success(`Venta #${pick.ficha} actualizada`, {
          description: "Puedes reenviar el comprobante actualizado",
          action: { label: "Comprobante WhatsApp", onClick: () => openReceipt(sale) },
          duration: 10000,
        });
      } else if (pick.mode === "reserved") {
        const { data: sale } = await api.post(`/reservations/${pick.ficha}/confirm`, {
          buyer_name: pick.buyer_name,
          buyer_phone: pick.buyer_phone,
          payment_method: pick.payment_method,
        });
        toast.success(`Venta confirmada · Ficha #${pick.ficha}`, {
          description: "Envía el comprobante al comprador",
          action: { label: "Comprobante WhatsApp", onClick: () => openReceipt(sale) },
          duration: 12000,
        });
      }
      closePick();
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPickBusy(false);
    }
  };

  const deleteSale = async (ficha) => {
    if (!window.confirm(`¿Anular definitivamente la venta de la ficha #${ficha}? La ficha volverá a estar disponible.`)) return;
    try {
      await api.delete(`/admin/sales/${ficha}`);
      toast.success(`Venta #${ficha} anulada`);
      closePick();
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmReservationRow = async (ficha) => {
    setBusyRes(ficha);
    try {
      const { data: sale } = await api.post(`/reservations/${ficha}/confirm`, { payment_method: resPay[ficha] || "Efectivo" });
      toast.success(`Venta confirmada · Ficha #${String(ficha).padStart(4, "0")}`, {
        description: "Envía el comprobante al comprador",
        action: { label: "Comprobante WhatsApp", onClick: () => openReceipt(sale) },
        duration: 12000,
      });
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyRes("");
    }
  };

  const createSeller = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/sellers", form);
      toast.success(`Vendedor ${form.name} creado`);
      setForm({ name: "", email: "", password: "", codigo_referido: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  const saveSellerEdit = async (e) => {
    e.preventDefault();
    if (!editSeller) return;
    try {
      await api.patch(`/admin/sellers/${editSeller.id}`, {
        name: editSeller.name,
        codigo_referido: editSeller.codigo_referido,
      });
      toast.success("Vendedor actualizado");
      setEditSeller(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleSeller = async (id) => {
    try {
      await api.patch(`/admin/sellers/${id}/toggle`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveCfg = async (e) => {
    e.preventDefault();
    setSavingCfg(true);
    try {
      await api.put("/stats", { total: Number(cfg.total), sold: Number(cfg.sold) });
      toast.success("Contador público actualizado");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingCfg(false);
    }
  };

  const inputCls =
    "w-full bg-[#051A0F] border border-[#F0B91B]/20 rounded-xl px-4 py-3 text-[#FDFBF7] font-outfit placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#F0B91B] transition-colors";

  const commissionPerFicha = summary?.commission_per_ficha || 10000;
  const totalCommission = summary?.total_commission || 0;

  const cards = summary
    ? [
        { icon: ShoppingBag, label: "Fichas vendidas", value: summary.total_sales, color: "#F0B91B" },
        { icon: DollarSign, label: "Total recaudado", value: formatCOP(summary.total_amount), color: "#7FD1AE", shimmer: true },
        { icon: Wallet, label: `Comisión vendedores (${formatCOP(commissionPerFicha)}/ficha)`, value: formatCOP(totalCommission), color: "#F0B91B", shimmer: true },
        { icon: Users, label: "Vendedores", value: summary.sellers_count, color: "#E63946" },
      ]
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#051A0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F0B91B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#051A0F] grain-overlay" data-testid="admin-dashboard">
      <header className="border-b border-[#F0B91B]/15 bg-[#0A2818]/60 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0B91B] to-[#E5A200] flex items-center justify-center">
              <Shield className="w-4 h-4 text-[#051A0F]" />
            </div>
            <div>
              <div className="font-bebas text-xl text-[#F0B91B] tracking-widest leading-none">BETMAR</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#FDFBF7]/50 font-outfit">Administración</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={scrollToReservations}
              className="relative inline-flex items-center gap-1.5 text-[#FDFBF7]/70 hover:text-[#F0B91B] font-outfit transition-colors"
              title="Ir a reservas activas"
              data-testid="admin-alerts-btn"
            >
              <Bell className={`w-5 h-5 ${newCount > 0 ? "text-[#F0B91B]" : ""}`} />
              {newCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
                  data-testid="admin-alerts-count"
                >
                  {newCount > 9 ? "9+" : newCount}
                </span>
              )}
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-[#FDFBF7]/70 hover:text-[#E63946] font-outfit transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-summary">
          {cards.map((c, i) => (
            <div key={i} className="rounded-2xl border p-5" style={{ borderColor: `${c.color}40`, background: "rgba(10,40,24,0.7)" }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${c.color}20` }}>
                <c.icon className="w-5 h-5" style={{ color: c.color }} />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/50 font-outfit leading-tight">{c.label}</div>
              <div className={`font-bebas text-3xl tracking-wide leading-none mt-1 ${c.shimmer ? "gold-shimmer" : "text-[#FDFBF7]"}`}>
                {c.value}
              </div>
            </div>
          ))}
        </div>

        {/* Web Push mobile notifications setup */}
        <section
          className="rounded-2xl border border-[#F0B91B]/30 bg-gradient-to-br from-[#0A2818] to-[#0A2818]/70 p-5 md:p-6 flex flex-wrap items-center justify-between gap-4"
          data-testid="push-setup"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${push.subscribed ? "bg-[#7FD1AE]/20" : "bg-[#F0B91B]/15"}`}>
              {push.subscribed ? (
                <BellRing className="w-6 h-6 text-[#7FD1AE]" />
              ) : (
                <Smartphone className="w-6 h-6 text-[#F0B91B]" />
              )}
            </div>
            <div className="min-w-0">
              <h2 className="font-bebas text-xl md:text-2xl text-[#FDFBF7] tracking-wider leading-tight">
                Notificaciones al celular
              </h2>
              <p className="text-xs md:text-sm text-[#FDFBF7]/65 font-outfit mt-1 max-w-lg">
                {!push.supported
                  ? "Tu navegador no soporta notificaciones push. Usa Chrome, Edge o Safari 16.4+."
                  : push.subscribed
                  ? "Recibirás una notificación en este dispositivo cuando alguien aparte una ficha o pague por Wompi."
                  : "Instala esta página en tu celular y activa las notificaciones. Recibirás avisos de nuevas reservas y ventas incluso con el navegador cerrado."}
              </p>
              {push.subscribed && (
                <p className="text-[10px] uppercase tracking-widest text-[#7FD1AE] font-outfit font-bold mt-2 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7FD1AE] animate-pulse" />
                  Activadas en este dispositivo
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {push.subscribed ? (
              <>
                <button
                  onClick={push.sendTest}
                  className="inline-flex items-center gap-2 bg-[#0A2818] border border-[#F0B91B]/40 hover:border-[#F0B91B] text-[#F0B91B] px-4 py-2.5 rounded-xl font-bebas text-sm tracking-wider transition-all"
                  data-testid="push-test-btn"
                >
                  <Bell className="w-4 h-4" />
                  Enviar prueba
                </button>
                <button
                  onClick={push.unsubscribe}
                  disabled={push.loading}
                  className="inline-flex items-center gap-2 text-[#FDFBF7]/50 hover:text-[#E63946] px-3 py-2.5 rounded-xl font-outfit text-sm transition-colors"
                  data-testid="push-off-btn"
                >
                  <BellOff className="w-4 h-4" />
                  Desactivar
                </button>
              </>
            ) : (
              <button
                onClick={push.subscribe}
                disabled={push.loading || !push.supported}
                className="inline-flex items-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] disabled:opacity-60 text-[#051A0F] px-5 py-3 rounded-xl font-bebas text-lg tracking-wider shadow-[0_10px_30px_-10px_rgba(240,185,27,0.75)] hover:-translate-y-0.5 transition-all"
                data-testid="push-on-btn"
              >
                {push.loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <BellRing className="w-5 h-5" />}
                Activar notificaciones
              </button>
            )}
          </div>
        </section>

        {/* Register / sell ficha (admin) */}
        <section className="rounded-2xl border border-[#7FD1AE]/25 bg-[#0A2818]/60 p-6 md:p-8" data-testid="admin-sale-section">
          <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#7FD1AE]" /> Registrar / vender ficha
          </h2>
          <p className="text-sm text-[#FDFBF7]/60 font-outfit mt-2">
            Registra una venta directa una vez confirmado el pago. También puedes hacer clic sobre un número en el mapa para <strong className="text-[#7FD1AE]">registrar</strong>, <strong className="text-[#F0B91B]">editar</strong> o <strong className="text-[#E63946]">anular</strong>.
          </p>
          <form onSubmit={registerSale} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-sale-form">
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDFBF7]/40" />
              <input value={saleForm.ficha_number} onChange={(e) => setSaleForm({ ...saleForm, ficha_number: e.target.value })} placeholder="Número de ficha (0000–9999)" className={inputCls + " pl-10"} data-testid="admin-sale-ficha" inputMode="numeric" maxLength={4} />
            </div>
            <input value={saleForm.buyer_name} onChange={(e) => setSaleForm({ ...saleForm, buyer_name: e.target.value })} placeholder="Nombre del comprador" className={inputCls} data-testid="admin-sale-buyer" />
            <input value={saleForm.buyer_phone} onChange={(e) => setSaleForm({ ...saleForm, buyer_phone: e.target.value })} placeholder="Teléfono (opcional)" className={inputCls} data-testid="admin-sale-phone" />
            <select value={saleForm.payment_method} onChange={(e) => setSaleForm({ ...saleForm, payment_method: e.target.value })} className={inputCls} data-testid="admin-sale-payment">
              {PAYMENTS.map((p) => <option key={p} value={p} className="bg-[#0A2818]">{p}</option>)}
            </select>
            <button type="submit" disabled={savingSale} className="md:col-span-2 inline-flex items-center justify-center gap-2 bg-[#7FD1AE] hover:bg-[#6BC49E] disabled:opacity-60 text-[#051A0F] py-3.5 rounded-xl font-bebas text-xl tracking-wider transition-all hover:-translate-y-0.5" data-testid="admin-sale-submit">
              {savingSale ? <Loader2 className="w-5 h-5 animate-spin" /> : `Registrar venta · ${formatCOP(50000)}`}
            </button>
          </form>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Create seller */}
          <section className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 p-6 md:p-8">
            <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#F0B91B]" /> Crear vendedor
            </h2>
            <form onSubmit={createSeller} className="mt-6 space-y-4" data-testid="seller-form">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre completo" className={inputCls} required data-testid="seller-name-input" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo" className={inputCls} required data-testid="seller-email-input" />
              <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Contraseña (mín. 6)" className={inputCls} required data-testid="seller-password-input" />
              <input type="text" value={form.codigo_referido} onChange={(e) => setForm({ ...form, codigo_referido: e.target.value.toUpperCase() })} placeholder="Código de referido (ej: VEN01)" className={inputCls + " uppercase tracking-widest"} data-testid="seller-codigo-input" maxLength={20} />
              <button type="submit" disabled={creating} className="w-full inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] disabled:opacity-60 text-[#051A0F] py-3.5 rounded-xl font-bebas text-xl tracking-wider transition-all hover:-translate-y-0.5" data-testid="seller-submit">
                {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : "Crear acceso"}
              </button>
            </form>
          </section>

          {/* Public counter config */}
          <section className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 p-6 md:p-8">
            <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#F0B91B]" /> Contador público
            </h2>
            <p className="text-sm text-[#FDFBF7]/60 font-outfit mt-2">
              El contador de la landing muestra <strong className="text-[#F0B91B]">base + ventas reales</strong>.
            </p>
            <form onSubmit={saveCfg} className="mt-6 space-y-4" data-testid="cfg-form">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#F0B91B] font-outfit font-semibold mb-2">Fichas totales</label>
                <input type="number" value={cfg.total} onChange={(e) => setCfg({ ...cfg, total: e.target.value })} className={inputCls} data-testid="cfg-total" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#F0B91B] font-outfit font-semibold mb-2">Base de fichas vendidas</label>
                <input type="number" value={cfg.sold} onChange={(e) => setCfg({ ...cfg, sold: e.target.value })} className={inputCls} data-testid="cfg-sold" />
              </div>
              <button type="submit" disabled={savingCfg} className="w-full inline-flex items-center justify-center gap-2 bg-[#0A2818] border border-[#F0B91B]/40 hover:border-[#F0B91B] text-[#F0B91B] py-3 rounded-xl font-bebas text-lg tracking-wider transition-all" data-testid="cfg-submit">
                {savingCfg ? <Loader2 className="w-5 h-5 animate-spin" /> : "Guardar"}
              </button>
            </form>
          </section>
        </div>

        {/* Site images */}
        <SiteImagesManager />

        {/* Ficha map — admin uses onCellClick for register/edit/manage_reserved */}
        <FichaMap refreshKey={mapKey} onCellClick={handleCellClick} />

        {/* Active reservations */}
        <section id="admin-reservations-anchor" className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 overflow-hidden" data-testid="admin-reservations">
          <div className="px-6 py-4 border-b border-[#F0B91B]/15">
            <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider">Reservas activas</h2>
          </div>
          {reservations.length === 0 ? (
            <div className="p-8 text-center text-[#FDFBF7]/50 font-outfit text-sm">No hay reservas activas.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-reservations-table">
                <thead>
                  <tr className="text-left text-[#F0B91B] font-outfit uppercase text-[10px] tracking-widest border-b border-[#F0B91B]/10">
                    <th className="px-6 py-3">Ficha</th>
                    <th className="px-6 py-3">Vendedor</th>
                    <th className="px-6 py-3">Comprador</th>
                    <th className="px-6 py-3">Expira</th>
                    <th className="px-6 py-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody className="font-outfit text-[#FDFBF7]/85">
                  {reservations.map((r) => (
                    <tr key={r.ficha_number} className="border-b border-[#F0B91B]/5 hover:bg-[#F0B91B]/5 transition-colors">
                      <td className="px-6 py-3 font-bebas text-lg tracking-wider text-[#F0B91B]">#{r.ficha_number}</td>
                      <td className="px-6 py-3">{r.seller_name}</td>
                      <td className="px-6 py-3 text-[#FDFBF7]/60">{r.buyer_name || "—"}</td>
                      <td className="px-6 py-3 text-[#FDFBF7]/60">{new Date(r.expires_at).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2 justify-center">
                          <select
                            value={resPay[r.ficha_number] || "Efectivo"}
                            onChange={(e) => setResPay({ ...resPay, [r.ficha_number]: e.target.value })}
                            className="bg-[#051A0F] border border-[#F0B91B]/20 rounded-lg px-2 py-1.5 text-[#FDFBF7] font-outfit text-xs focus:outline-none focus:border-[#F0B91B]"
                            data-testid={`admin-res-payment-${r.ficha_number}`}
                          >
                            {PAYMENTS.map((p) => <option key={p} value={p} className="bg-[#0A2818]">{p}</option>)}
                          </select>
                          <button onClick={() => confirmReservationRow(r.ficha_number)} disabled={busyRes === r.ficha_number} className="inline-flex items-center gap-1 text-xs bg-[#7FD1AE] hover:bg-[#6BC49E] disabled:opacity-60 text-[#051A0F] px-3 py-1.5 rounded-lg font-bebas tracking-wider transition-colors" data-testid={`admin-confirm-res-${r.ficha_number}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Confirmar
                          </button>
                          <button onClick={() => cancelReservation(r.ficha_number)} className="inline-flex items-center gap-1 text-xs text-[#E63946] hover:underline" data-testid={`admin-cancel-res-${r.ficha_number}`}>
                            Cancelar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Sellers list — now with Comisión column */}
        <section className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 overflow-hidden">
          <div className="px-6 py-4 border-b border-[#F0B91B]/15 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider">Vendedores</h2>
            <span className="text-[11px] uppercase tracking-widest text-[#F0B91B]/80 font-outfit">
              Comisión: <strong className="gold-shimmer">{formatCOP(commissionPerFicha)}</strong> / ficha
            </span>
          </div>
          {sellers.length === 0 ? (
            <div className="p-10 text-center text-[#FDFBF7]/50 font-outfit">No hay vendedores todavía.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sellers-table">
                <thead>
                  <tr className="text-left text-[#F0B91B] font-outfit uppercase text-[10px] tracking-widest border-b border-[#F0B91B]/10">
                    <th className="px-6 py-3">Nombre</th>
                    <th className="px-6 py-3">Correo</th>
                    <th className="px-6 py-3">Código</th>
                    <th className="px-6 py-3 text-center">Ventas</th>
                    <th className="px-6 py-3 text-right">Recaudado</th>
                    <th className="px-6 py-3 text-right">Comisión</th>
                    <th className="px-6 py-3 text-center">Estado</th>
                    <th className="px-6 py-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody className="font-outfit text-[#FDFBF7]/85">
                  {sellers.map((s) => (
                    <tr key={s.id} className="border-b border-[#F0B91B]/5 hover:bg-[#F0B91B]/5 transition-colors">
                      <td className="px-6 py-3 font-medium text-[#FDFBF7]">{s.name}</td>
                      <td className="px-6 py-3 text-[#FDFBF7]/60">{s.email}</td>
                      <td className="px-6 py-3">
                        {s.codigo_referido ? (
                          <span className="inline-flex items-center gap-1 bg-[#F0B91B]/15 text-[#F0B91B] px-2 py-1 rounded-md font-bebas tracking-widest text-xs" data-testid={`codigo-${s.id}`}>
                            {s.codigo_referido}
                          </span>
                        ) : (
                          <span className="text-[#FDFBF7]/30 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-center font-bebas text-lg text-[#F0B91B]">{s.sales_count}</td>
                      <td className="px-6 py-3 text-right">{formatCOP(s.sales_amount)}</td>
                      <td className="px-6 py-3 text-right font-bebas text-lg gold-shimmer" data-testid={`commission-${s.id}`}>{formatCOP(s.commission || 0)}</td>
                      <td className="px-6 py-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${s.active ? "bg-[#7FD1AE]/15 text-[#7FD1AE]" : "bg-[#E63946]/15 text-[#E63946]"}`}>
                          {s.active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <button
                            onClick={() => setEditSeller({ id: s.id, name: s.name, codigo_referido: s.codigo_referido || "" })}
                            className="inline-flex items-center gap-1 text-xs text-[#F0B91B] hover:bg-[#F0B91B]/10 px-2 py-1.5 rounded-lg transition-colors"
                            data-testid={`edit-seller-${s.id}`}
                            title="Editar código y nombre"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => toggleSeller(s.id)} className="inline-flex items-center gap-1 text-xs text-[#FDFBF7]/60 hover:text-[#F0B91B] transition-colors" data-testid={`toggle-${s.id}`}>
                            <Power className="w-3.5 h-3.5" /> {s.active ? "Desactivar" : "Activar"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* All sales — with edit/delete actions */}
        <section className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 overflow-hidden">
          <div className="px-6 py-4 border-b border-[#F0B91B]/15">
            <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider">Todas las ventas</h2>
          </div>
          {sales.length === 0 ? (
            <div className="p-10 text-center text-[#FDFBF7]/50 font-outfit">No hay ventas registradas.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="all-sales-table">
                <thead>
                  <tr className="text-left text-[#F0B91B] font-outfit uppercase text-[10px] tracking-widest border-b border-[#F0B91B]/10">
                    <th className="px-6 py-3">Ficha</th>
                    <th className="px-6 py-3">Comprador</th>
                    <th className="px-6 py-3">Vendedor</th>
                    <th className="px-6 py-3">Cód. Ref.</th>
                    <th className="px-6 py-3">Método</th>
                    <th className="px-6 py-3 text-right">Monto</th>
                    <th className="px-6 py-3">Fecha</th>
                    <th className="px-6 py-3 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="font-outfit text-[#FDFBF7]/85">
                  {sales.map((s) => (
                    <tr key={s.id} className="border-b border-[#F0B91B]/5 hover:bg-[#F0B91B]/5 transition-colors">
                      <td className="px-6 py-3 font-bebas text-lg tracking-wider text-[#F0B91B]">#{s.ficha_number}</td>
                      <td className="px-6 py-3">{s.buyer_name}</td>
                      <td className="px-6 py-3 text-[#FDFBF7]/60">{s.seller_name}</td>
                      <td className="px-6 py-3">
                        {s.codigo_referido ? (
                          <span className="inline-flex bg-[#F0B91B]/15 text-[#F0B91B] px-2 py-1 rounded-md font-bebas tracking-widest text-xs" data-testid={`sale-codigo-${s.ficha_number}`}>
                            {s.codigo_referido}
                          </span>
                        ) : (
                          <span className="text-[#FDFBF7]/30 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3">{s.payment_method}</td>
                      <td className="px-6 py-3 text-right">{formatCOP(s.amount)}</td>
                      <td className="px-6 py-3 text-[#FDFBF7]/60">{new Date(s.created_at).toLocaleDateString("es-CO")}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1 justify-center">
                          <button onClick={() => openReceipt(s)} title="Enviar comprobante por WhatsApp" className="inline-flex items-center gap-1 text-xs text-[#25D366] hover:bg-[#25D366]/10 px-2 py-1.5 rounded-lg transition-colors" data-testid={`admin-receipt-${s.ficha_number}`}>
                            <MessageCircle className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleCellClick({ ficha: s.ficha_number, state: "sold", info: s })} title="Editar venta" className="inline-flex items-center gap-1 text-xs text-[#F0B91B] hover:bg-[#F0B91B]/10 px-2 py-1.5 rounded-lg transition-colors" data-testid={`admin-edit-${s.ficha_number}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteSale(s.ficha_number)} title="Anular venta" className="inline-flex items-center gap-1 text-xs text-[#E63946] hover:bg-[#E63946]/10 px-2 py-1.5 rounded-lg transition-colors" data-testid={`admin-delete-${s.ficha_number}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {/* Edit seller (name + codigo_referido) */}
      {editSeller && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-seller-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditSeller(null)} />
          <form
            onSubmit={saveSellerEdit}
            className="relative w-full max-w-md rounded-3xl border border-[#F0B91B]/30 bg-[#0A2818] p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider">Editar vendedor</h3>
              <button type="button" onClick={() => setEditSeller(null)} className="text-[#FDFBF7]/50 hover:text-[#E63946]" data-testid="edit-seller-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#F0B91B] font-outfit font-bold mb-1.5">Nombre</label>
              <input value={editSeller.name} onChange={(e) => setEditSeller({ ...editSeller, name: e.target.value })} className={inputCls} data-testid="edit-seller-name" required />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#F0B91B] font-outfit font-bold mb-1.5">Código de referido</label>
              <input value={editSeller.codigo_referido} onChange={(e) => setEditSeller({ ...editSeller, codigo_referido: e.target.value.toUpperCase() })} placeholder="Ej: VEN01 (déjalo vacío para quitar)" className={inputCls + " uppercase tracking-widest"} maxLength={20} data-testid="edit-seller-codigo" />
              <p className="text-[10px] text-[#FDFBF7]/50 font-outfit mt-1">
                Comparte este código con tu vendedor. Los compradores lo usarán en la landing para asignarle la venta automáticamente.
              </p>
            </div>
            <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] py-3.5 rounded-xl font-bebas text-lg tracking-wider transition-all" data-testid="edit-seller-submit">
              Guardar cambios
            </button>
          </form>
        </div>
      )}

      {/* Contextual modal — register / edit / manage reservation */}
      {pick.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-pick-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closePick} />
          <div className="relative w-full max-w-md rounded-3xl border border-[#F0B91B]/30 bg-[#0A2818] p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider">
                {pick.mode === "register" && <>Registrar compra · <span className="text-[#F0B91B]">#{pick.ficha}</span></>}
                {pick.mode === "edit" && <>Editar venta · <span className="text-[#F0B91B]">#{pick.ficha}</span></>}
                {pick.mode === "reserved" && <>Confirmar reserva · <span className="text-[#F0B91B]">#{pick.ficha}</span></>}
              </h3>
              <button onClick={closePick} className="text-[#FDFBF7]/50 hover:text-[#E63946]" data-testid="pick-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {pick.mode === "edit" && (
              <div className="mb-4 rounded-xl border border-[#F0B91B]/20 bg-[#F0B91B]/5 px-3 py-2 text-xs text-[#F0B91B] font-outfit flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Modifica los datos y guarda, o pulsa <strong>Anular venta</strong> para liberar la ficha.</span>
              </div>
            )}

            <form onSubmit={submitPick} className="space-y-4" data-testid="pick-form">
              <input value={pick.buyer_name} onChange={(e) => setPick({ ...pick, buyer_name: e.target.value })} placeholder="Nombre del comprador" className={inputCls} autoFocus data-testid="pick-buyer" />
              <input value={pick.buyer_phone} onChange={(e) => setPick({ ...pick, buyer_phone: e.target.value })} placeholder="Teléfono (opcional, para el comprobante)" className={inputCls} data-testid="pick-phone" />
              <select value={pick.payment_method} onChange={(e) => setPick({ ...pick, payment_method: e.target.value })} className={inputCls} data-testid="pick-payment">
                {PAYMENTS.map((p) => <option key={p} value={p} className="bg-[#0A2818]">{p}</option>)}
              </select>

              {pick.mode === "edit" && sellers.length > 0 && (
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#F0B91B] font-outfit font-semibold mb-1.5">Reasignar vendedor (opcional)</label>
                  <select value={pick.seller_id} onChange={(e) => setPick({ ...pick, seller_id: e.target.value })} className={inputCls} data-testid="pick-seller">
                    <option value="" className="bg-[#0A2818]">— Mantener actual —</option>
                    {sellers.map((s) => (
                      <option key={s.id} value={s.id} className="bg-[#0A2818]">{s.name} ({s.email})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className={`grid ${pick.mode === "edit" ? "grid-cols-2" : "grid-cols-1"} gap-3 pt-1`}>
                <button
                  type="submit"
                  disabled={pickBusy}
                  className="inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] disabled:opacity-60 text-[#051A0F] py-3.5 rounded-xl font-bebas text-lg md:text-xl tracking-wider transition-all"
                  data-testid="pick-submit"
                >
                  {pickBusy ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : pick.mode === "register" ? (
                    `Registrar · ${formatCOP(50000)}`
                  ) : pick.mode === "edit" ? (
                    "Guardar cambios"
                  ) : (
                    "Confirmar venta"
                  )}
                </button>
                {pick.mode === "edit" && (
                  <button
                    type="button"
                    onClick={() => deleteSale(pick.ficha)}
                    className="inline-flex items-center justify-center gap-2 bg-[#0A2818] border border-[#E63946]/60 hover:border-[#E63946] hover:bg-[#E63946]/10 text-[#E63946] py-3.5 rounded-xl font-bebas text-lg tracking-wider transition-all"
                    data-testid="pick-delete"
                  >
                    <Trash2 className="w-4 h-4" />
                    Anular venta
                  </button>
                )}
                {pick.mode === "reserved" && (
                  <button
                    type="button"
                    onClick={() => { cancelReservation(pick.ficha); closePick(); }}
                    className="inline-flex items-center justify-center gap-2 text-[#E63946] hover:underline text-sm font-outfit"
                    data-testid="pick-cancel-reservation"
                  >
                    Cancelar reserva
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
